import { useContext, useEffect, useState } from 'react';

import styled, { css } from 'styled-components';

import countries from '../../assets/countriesPrefix.json';
import { getFlagName } from '../../helpers/countries.helpers';

import { CountryPrefixPickerProps, Country, CountryOptionProps } from './CountryPrefixPicker.decl';

import { IconContext, Icon, Grid, Typography, useTheme } from '@aircall/tractor';
import { Select } from '@components/Select';
import { sortAscending } from '@dashboard/library';
import { variable } from '@helpers/style.helpers';

const CountryOptionWrapper = styled(Grid)<Pick<CountryOptionProps, 'isOptionLabel'>>`
  align-items: center;
  ${({ isOptionLabel }) => {
    if (isOptionLabel) {
      return css`
        display: flex;

        > ${Typography} {
          margin-left: ${variable.space.xxs}px;
        }
      `;
    }

    return css`
      grid-auto-flow: column;
      grid-gap: ${variable.space.xxs}px;
    `;
  }}
`;

function CountryOption({
  dial_code: dialCode,
  name: countryName,
  code,
  isOptionLabel,
  dialCodeFirst,
}: CountryOptionProps) {
  const { space } = useTheme();
  const iconContext = useContext(IconContext);
  const flags = iconContext!.icons!;
  const flag = flags[getFlagName(code.toUpperCase())];
  return (
    <CountryOptionWrapper isOptionLabel={isOptionLabel}>
      <Icon component={flag} size={isOptionLabel ? space.m : space.l} />
      <Typography variant='body2'>
        {dialCodeFirst && dialCode} {countryName} {!dialCodeFirst && dialCode && `(${dialCode})`}
      </Typography>
    </CountryOptionWrapper>
  );
}

const _countryOptions: Country[] = [...countries].sort((a, b) => sortAscending(a.name, b.name));

const getOptionValue = ({ code }: Country) => code;

const getOptionLabel = (dialCodeFirst: boolean, isOptionLabel = true) =>
  function CountryOptionLabel(country: Country) {
    return (
      <CountryOption isOptionLabel={isOptionLabel} {...country} dialCodeFirst={dialCodeFirst} />
    );
  };

export function CountryPrefixPicker({
  onChange,
  name,
  'data-test': dataTest = 'country-prefix-picker',
  placeholder,
  defaultSelected,
  countryOptions = _countryOptions,
  dialCodeFirst = true,
  ...otherProps
}: CountryPrefixPickerProps) {
  const [options, setOptions] = useState(countryOptions);
  useEffect(() => {
    setOptions(countryOptions);
  }, [countryOptions]);
  const onSearch = (text: string) => {
    const searchText = text.trim().toLowerCase();
    setOptions(
      searchText
        ? countryOptions.filter(
            ({ dial_code: dialCode, name: _name }) =>
              _name.toLowerCase().includes(searchText) || dialCode?.includes(searchText)
          )
        : countryOptions
    );
  };

  return (
    <Select
      name={name}
      onChange={onChange}
      getOptionValue={getOptionValue}
      getOptionLabel={getOptionLabel(dialCodeFirst) as unknown as () => string}
      options={options}
      data-test={dataTest}
      placeholder={placeholder}
      value={defaultSelected}
      onSearch={onSearch}
      searchable
      components={{
        Option: getOptionLabel(dialCodeFirst, false),
      }}
      {...otherProps}
    />
  );
}
